// ============================================================
// Viashift — Inventory helpers (Phase 2, Epic 2A)
// ============================================================
// Centralized derived-state helpers for inventory rows. The
// schema does not store `status` (stocked/low/critical/dead) —
// it's computed from `products.count`, `products.par`, and
// `products.dead_stock` per the demo's logic.
//
// Per ticket 2A.4 we may move this into a Postgres function if
// drift between client computations becomes an issue. For now
// the source of truth is here, and every consumer (summary
// pills, row dot, future audit page) reads from this helper.
// ============================================================

import type { Database } from './database.types';

export type ProductRow = Database['public']['Tables']['products']['Row'];
export type CategoryRow = Database['public']['Tables']['categories']['Row'];

/**
 * Computed status of a product row.
 *
 *   - 'dead'     — `dead_stock = true` (long-disused inventory)
 *   - 'critical' — count is zero, OR count is 1 when par ≥ 2
 *   - 'low'      — count is below par (and not already critical)
 *   - 'stocked'  — count meets/exceeds par, OR no par set
 *
 * Precedence: dead → critical → low → stocked. The `dead_stock`
 * flag wins regardless of count vs par because dead inventory
 * isn't part of the working stock pool — surfacing it as
 * "stocked" or "low" would mislead the summary pills.
 *
 * The "1 left when par ≥ 2" critical rule (matches the design
 * reference) recognizes that being one tap from empty is a
 * different urgency than being one tap below par. Without it,
 * count=3 par=4 and count=1 par=4 both render as 'low' — but
 * the second is reorder-now, not reorder-soon. The rule only
 * fires when par is at least 2; count=1 par=1 stays 'stocked'
 * (at par, not below).
 *
 * Recomputation: status is pure-derived on every render. Any
 * count change that flows through React state (e.g. the
 * optimistic update in 2A.3's adjustCount) re-renders the row
 * and re-derives status with no extra wiring. Per ticket 2A.4
 * we may move this into a Postgres function if drift between
 * client computations becomes an issue (two clients showing
 * different status for the same product) — that hasn't
 * happened, so the client-side path stays.
 *
 * Kanban categories (C/D/E) seed with `par = null` and
 * `count = 0` because kanban tracks "tagged for reorder," not
 * on-hand counts. They render as 'stocked' here until tag-based
 * status arrives in a later ticket.
 */
export type ProductStatus = 'stocked' | 'low' | 'critical' | 'dead';

export function computeProductStatus(
  product: Pick<ProductRow, 'count' | 'par' | 'dead_stock'>,
): ProductStatus {
  if (product.dead_stock) return 'dead';
  if (product.par === null) return 'stocked';
  if (product.count <= 0) return 'critical';
  // One-unit-left when par calls for two or more is critical,
  // not low — matches the design reference's status rule and
  // preserves the reorder-now signal that pure `count < par`
  // would lose. See the docstring above for full rationale.
  if (product.count === 1 && product.par >= 2) return 'critical';
  if (product.count < product.par) return 'low';
  return 'stocked';
}

/**
 * Tracking modes from `categories.tracking_mode`. Stored as
 * text in Postgres with a CHECK constraint pinning the values;
 * the generated DB type is `string` because CHECK constraints
 * don't propagate. Narrow at the query boundary.
 */
export type TrackingMode = 'count' | 'kanban';

export function isTrackingMode(value: string): value is TrackingMode {
  return value === 'count' || value === 'kanban';
}

// ============================================================
// Days-of-supply helper (Phase 2, Epic 2A.5)
// ============================================================
// Monthly-rate aggregation lives in the `product_burn_rates`
// materialized view as of 4A.1 (2026-05-16). The view is
// refreshed nightly by pg_cron; consumers read it directly.
// `computeMonthlyBurnRate` and `BURN_RATE_WINDOW_DAYS` were
// removed in the 4A.1 swap — see git history if needed.
//
// `computeDaysOfSupply` stays here because it composes a rate
// (from the view) with the current count (from the product
// row), which is a view-of-multiple-sources operation the
// caller does at render time.

/**
 * Days of supply at current burn rate. Returns `null` when the
 * monthly rate is null/zero (no usage history) — infinite
 * supply is the right semantic answer but renders as a strange
 * label. Caller hides the label in that case.
 */
export function computeDaysOfSupply(
  count: number,
  monthlyRate: number | null,
): number | null {
  if (monthlyRate === null || monthlyRate <= 0) return null;
  if (count <= 0) return 0;
  return Math.round((count / monthlyRate) * 30);
}

/**
 * Format a monthly burn rate for display. Matches the design
 * reference: integer rates show as "~3/mo", fractional rates
 * one decimal "~2.4/mo".
 */
export function formatBurnRate(monthlyRate: number): string {
  if (monthlyRate === Math.floor(monthlyRate)) {
    return `~${monthlyRate}/mo`;
  }
  return `~${monthlyRate.toFixed(1)}/mo`;
}

/** Days-of-supply threshold below which the label renders amber. */
export const DOS_AMBER_THRESHOLD_DAYS = 14;


// ============================================================
// Par auto-suggestion (Phase 4, Epic 4A.3)
// ============================================================
// Surfaces a "Bump par 4 → 6" hint on InventoryPage rows when
// current par diverges meaningfully from the burn-rate-implied
// target. Math is intentionally client-side (Fork 6 of the
// 2026-05-20 DECISIONS entry) — small, stateless, and tuning
// the constants below is a code change without a migration.
//
// Suggestion target: a par level that gives ~PAR_SUGGESTION_
// TARGET_DAYS of supply at the current 4-week burn rate. This
// pairs naturally with the days-of-supply pill above —
// suggestion and DoS reading both anchor on the same constant
// family (consumption × days / 30).
//
// Gating rules (in order):
//   1. Need a current par (null → no comparison surface).
//   2. Need a non-null, non-zero monthlyRate (no usage history).
//   3. Need MIN_UNITS_4W qualifying-consumption units in the
//      window — suppresses suggestions on too-thin data
//      (a single scan in 28 days isn't enough signal to act on).
//   4. |suggested - current| must clear both the absolute floor
//      (MIN_DELTA units) and the relative threshold (% of
//      current par). The relative check is skipped when current
//      par is 0 (avoid division-by-zero; the absolute floor
//      catches it).
//   5. Dismissal: if the caller dismissed for this exact par
//      value, suppress. Any par change invalidates the dismissal
//      naturally (the check is current_par == dismissed_for_par,
//      not "ever dismissed").
//   6. Identity: never suggest no change.
//
// Returns null when no suggestion should surface, otherwise the
// integer target par.

/** Target days-of-supply for the auto-suggested par. */
export const PAR_SUGGESTION_TARGET_DAYS = 21;

/** Relative threshold: |suggested - current| / current must exceed this. */
export const PAR_SUGGESTION_THRESHOLD = 0.30;

/** Absolute threshold: |suggested - current| must be at least this many units. */
export const PAR_SUGGESTION_MIN_DELTA = 2;

/** Minimum 4-week consumption units required to surface a suggestion. */
export const PAR_SUGGESTION_MIN_UNITS_4W = 4;

export interface ParSuggestionInput {
  /** Current par on the product. Null products are excluded. */
  currentPar: number | null;
  /** Monthly burn rate from product_burn_rates view. */
  monthlyRate: number | null;
  /** 4-week qualifying-consumption units (for confidence gate). */
  units4w: number | null;
  /**
   * `dismissed_for_par` for this user × product, or null if the
   * caller has not dismissed any suggestion on this product.
   * Suppression valid only while `dismissedForPar === currentPar`.
   */
  dismissedForPar: number | null;
}

export function computeParSuggestion(input: ParSuggestionInput): number | null {
  const { currentPar, monthlyRate, units4w, dismissedForPar } = input;

  // Rule 1: current par required.
  if (currentPar === null) return null;

  // Rule 2: non-zero monthly rate required.
  if (monthlyRate === null || monthlyRate <= 0) return null;

  // Rule 3: confidence floor on units in the 4-week window.
  if (units4w === null || units4w < PAR_SUGGESTION_MIN_UNITS_4W) return null;

  // Compute target par as units/month * target_days / 30. Round
  // to nearest integer (par is whole units). Clamp at zero.
  const suggested = Math.max(
    0,
    Math.round((monthlyRate * PAR_SUGGESTION_TARGET_DAYS) / 30),
  );

  // Rule 6 (early): never suggest no change.
  if (suggested === currentPar) return null;

  const delta = Math.abs(suggested - currentPar);

  // Rule 4a: absolute floor.
  if (delta < PAR_SUGGESTION_MIN_DELTA) return null;

  // Rule 4b: relative threshold. Skipped when currentPar is 0 —
  // 0 → N is always "infinite" relative change, but the
  // absolute floor (rule 4a) already gated on |delta| >= 2.
  if (currentPar > 0) {
    const relative = delta / currentPar;
    if (relative < PAR_SUGGESTION_THRESHOLD) return null;
  }

  // Rule 5: dismissal with par-value invalidation.
  if (dismissedForPar !== null && dismissedForPar === currentPar) return null;

  return suggested;
}
